import { Component, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Title, Meta, DomSanitizer } from '@angular/platform-browser';
import { DeviceDetectorService } from 'ngx-device-detector';
import { DOWNLOAD_STATE_NAME, LAST_ACCESS_NAME, TIME_TO_RESHOW_DOWNLOAD_MODAL, TIME_TO_SHOW_HOME_MODALS } from '../../app.constants';
import { TranslocoService, TranslocoDirective } from '@jsverse/transloco';
import { UpperCasePipe, CommonModule } from '@angular/common';
import { LoadingComponent } from '../../components/loading/loading.component';
import { Bind } from 'primeng/bind';
import { Divider } from 'primeng/divider';
import { OfferCardComponent } from '../../components/offer-card/offer-card.component';
import { Button } from 'primeng/button';
import { OpeningHoursComponent } from '../../components/opening-hours/opening-hours.component';
import { Dialog } from 'primeng/dialog';
import { Message } from 'primeng/message';
import { SafeStylePipe } from '../../pipes/safe-style.pipe';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { ApiService } from '../../_utils/api/api.service';
import { slugify } from '../../_utils/functions';
import { InsightService } from '../../_utils/insight/insight.service';
import { PwaService } from '../../_utils/pwa/pwa.service';
import { SeoService } from '../../_utils/seo/seo.service';
import { StorageService } from '../../_utils/storage/storage.service';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
  imports: [CommonModule, TranslocoDirective, LoadingComponent, Bind, Divider, OfferCardComponent, Button, OpeningHoursComponent, Dialog, Message, UpperCasePipe, SafeStylePipe, TranslatePipe]
})
export class HomeComponent implements OnInit {
  error: string = "";
  loading = signal(false);
  data: any = null;
  offers: any[] = [];
  openingHours: any[] = [];
  downloadModal = signal<any>({ loading: false, visible: false, error: null, message: null, os: undefined });
  installedAppModal = signal<any>({ visible: false, url: null });
  newOffersModal = signal<any>({ loading: false, visible: false, error: null, newOffers: [] });
  chatModalVisible = signal(false);
  socials: any = null;
  shareEnabled: boolean = typeof navigator !== 'undefined' && navigator.share != undefined;

  constructor(private translocoService: TranslocoService, protected sanitizer: DomSanitizer, private seoService: SeoService, private pwaService: PwaService, private storageService: StorageService, private deviceDetectorService: DeviceDetectorService, private titleService: Title, private metaTagService: Meta, protected router: Router, private apiService: ApiService, protected insightService: InsightService) {
    this.downloadModal.update(modal => ({ ...modal, os: deviceDetectorService.os() }));
  }

  ngOnInit(): void {
    this.loadData();
  }

  showModals(): void {
    setTimeout(() => {
      if (this.pwaService.isInstalled()) {
        const last_access = this.storageService.getValue(LAST_ACCESS_NAME);
        if (last_access) { //only if is sets, otherwise is first access
          for (let offer of this.offers) {
            if (new Date(offer.updated_at).getTime() > last_access)
              this.newOffersModal.update(modal => ({ ...modal, newOffers: [...modal.newOffers, offer] }));
          }
          if (this.newOffersModal().newOffers.length > 0)
            this.newOffersModal.update(modal => ({ ...modal, visible: true }));
        }
        this.storageService.saveValue(LAST_ACCESS_NAME, Date.now()); //prevent a new open if the page is realoded
      } else {
        if (this.downloadModal().os == 'iOS' || this.downloadModal().os == 'Android') {
          //show modal only if is a new user and app is not installed
          this.pwaService.hasAlreadyInstalledApp().subscribe((url) => {
            if (url && typeof window != 'undefined') {
              this.installedAppModal.set({ url, visible: true });
            } else {
              const download_state = this.storageService.getValue(DOWNLOAD_STATE_NAME);
              if (!download_state || (download_state && download_state.status != 'installed' && (Date.now() - download_state.date) >= TIME_TO_RESHOW_DOWNLOAD_MODAL)) {
                this.downloadModal.update(modal => ({ ...modal, visible: true }));
              }
            }
          });
        }
      }
    }, TIME_TO_SHOW_HOME_MODALS);
  }

  openInstalledApp(): void {
    if (this.installedAppModal().url && typeof window != 'undefined')
      window.open(this.installedAppModal().url, '_blank');
  }

  dimissDownloadModal(): void {
    this.downloadModal.update(modal => ({ ...modal, visible: false }));
    this.storageService.saveValue(DOWNLOAD_STATE_NAME, { status: 'install-rejected', date: Date.now() });
  }

  downloadApp(): void {
    this.downloadModal.update(modal => ({ ...modal, error: null, message: null, loading: true }));
    this.pwaService.install().subscribe({
      next: (message) => {
        this.downloadModal.update(modal => ({ ...modal, loading: false, message }));
        this.storageService.saveValue(DOWNLOAD_STATE_NAME, { status: 'install-accepted', date: Date.now() });
      },
      error: (err) => {
        console.error(err);
        this.downloadModal.update(modal => ({ ...modal, error: err, loading: false }));
      },
      complete: () => {
        this.downloadModal.update(modal => ({ ...modal, visible: false }));
        this.storageService.saveValue(DOWNLOAD_STATE_NAME, { status: 'installed', date: Date.now() });
      }
    });
  }

  generateOfferUrl(offer: any): string {
    const translatePipe = new TranslatePipe(this.translocoService);
    return this.data.vanity_url ? slugify(translatePipe.transform(offer['titolo'])) : offer['id'];
  }

  share(): void {
    if (this.shareEnabled) {
      const translatePipe = new TranslatePipe(this.translocoService);
      let description = translatePipe.transform(this.data.description);

      const div = document.createElement('div');
      div.innerHTML = description;

      let share_data = {
        title: this.data.denominazione,
        text: div.textContent || div.innerText || description,
        url: this.data.pwa_url
      }

      if (navigator.canShare(share_data)) {
        navigator.share(share_data).then(() => {
          console.log('Sharing!');
        }).catch(console.error);
      }
    } else {
      console.error("Browser does not support sharing");
    }
  }

  protected openRegisterLink(refferal: string): void {
    if (refferal && typeof window != 'undefined') {
      window.open(refferal, '_blank');
    }
  }

  private loadData(): void {
    this.loading.set(true);
    this.apiService.getCompany().subscribe({
      next: (data) => {
        this.loading.set(false);
        const translatePipe = new TranslatePipe(this.translocoService);
        let description = translatePipe.transform(data.data.description);

        this.seoService.setSeo({
          title: data.data.denominazione,
          description: description || data.data.denominazione,
          image: data.data.image || data.data.logo,
          url: data.data.pwa_url
        });

        if (typeof window === 'undefined') //skip for SSR
          return;

        this.data = data.data;
        this.offers = data.data.offers;
        this.socials = JSON.parse(data.data.social_link);
        this.openingHours = JSON.parse(data.data.openinghours);
        this.showModals();
        this.insightService.send({
          event: 'open_page',
          event_name: 'home'
        });
      },
      error: (err) => {
        console.error(err);
        this.router.navigate(['error'], { queryParams: { message: err } });
      }
    });
  }
}
